import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";

@Injectable({
  providedIn: "root",
})
export class CommonService {
  constructor(private readonly http: HttpClient) {}

  getDisplayNames() {
    return this.http.get("display-names/");
  }

  getLabels() {
    const labels: any = localStorage.getItem("displayLabels");
    return JSON.parse(labels);
  }

  getOutlookFiles() {
    return this.http.get("nps/outlookfiles");
  }

  getLicensingImages(moduleName: string) {
    return this.http.get(`licensing-images/?module_name=${moduleName}`);
  }

  getAnalyticsEnabled() {
    return this.http.get("app-config/analytics_enabled/");
  }

  getAiroCueEnabled() {
    return this.http.get("app-config/airo_cue_enabled/");
  }

  getMaxFileSize(): Observable<number> {
    const storedValue = localStorage.getItem("max_upload_file_size");

    if (storedValue) {
      return new Observable((observer) => {
        observer.next(Number(storedValue));
        observer.complete();
      });
    }

    return this.http.get<any>(`upload-file-max-size/`).pipe(
      map((res) => {
        const size = Number(res?.value || 50000000);
        localStorage.setItem("max_upload_file_size", size.toString());
        return size;
      })
    );
  }

  validateFileSize(file: File): Observable<boolean> {
    return this.getMaxFileSize().pipe(
      map((maxSize: number) => {
        if (file.size > maxSize) {
          return false;
        }
        return true;
      })
    );
  }

  getZoneOwners() {
    return this.http.get("modules");
  }

  saveZoneOwners(payload: any) {
    return this.http.post("users/access-inheritance/", payload);
  }

  getMailingLists() {
    return this.http.get("notification-lists/");
  }

  saveMailingLists(payload: any) {
    return this.http.post("notification-lists/update-by-action/", payload);
  }

  getOptiqUrl() {
    return this.http.get("optiq-url/");
  }

  getZohoDashboardEnabled() {
    return this.http.get("app-config/useZohoDashboard/");
  }

  async ensureAppConfigLoaded(): Promise<void> {
    await Promise.all([
      this.ensureDisplayNames(),
      this.ensureAnalyticsEnabled(),
      this.ensureZohoDashboardEnabled(),
      this.ensureAiroCueEnabled(),
    ]);
  }

  private async ensureDisplayNames(): Promise<void> {
    if (localStorage.getItem("displayLabels")) {
      return;
    }
    try {
      const resp: any = await this.getDisplayNames().toPromise();
      localStorage.setItem("displayLabels", JSON.stringify(resp));
    } catch (error) {
      // Swallow so the other app-config calls can still complete.
    }
  }

  private async ensureAnalyticsEnabled(): Promise<void> {
    if (localStorage.getItem("analyticsEnabled")) {
      return;
    }
    try {
      const resp: any = await this.getAnalyticsEnabled().toPromise();
      localStorage.setItem("analyticsEnabled", resp.value);
    } catch (error) {
      // Swallow so the other app-config calls can still complete.
    }
  }

  private async ensureZohoDashboardEnabled(): Promise<void> {
    if (localStorage.getItem("zohoDashboardEnabled")) {
      return;
    }
    try {
      const resp: any = await this.getZohoDashboardEnabled().toPromise();
      localStorage.setItem("zohoDashboardEnabled", resp.value);
    } catch (error) {
      // Swallow so the other app-config calls can still complete.
    }
  }

  private async ensureAiroCueEnabled(): Promise<void> {
    if (localStorage.getItem("airoCueEnabled")) {
      return;
    }
    try {
      const resp: any = await this.getAiroCueEnabled().toPromise();
      localStorage.setItem("airoCueEnabled", resp.value);
    } catch (error) {
      // Swallow so the other app-config calls can still complete.
    }
  }
}
